import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import ImportZipModulariseReviewDialog from "./ImportZipModulariseReviewDialog";
import { runImportZipModulariseReview } from "../utils/importZipReviewApi";

const ImportZipDialog = ({
  open,
  onClose,
  selectedProjectId,
  activeChange,
  currentUsername,
  onImportedSaved, // optional callback after any save
}) => {
  const [zipFile, setZipFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [imported, setImported] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [creatingMap, setCreatingMap] = useState({}); // { [path]: true }
  const [createInfo, setCreateInfo] = useState("");

  const [changeSummary, setChangeSummary] = useState("Imported from ZIP");

  // diff preview state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffErr, setDiffErr] = useState("");
  const [diffText, setDiffText] = useState("");
  const [diffTitle, setDiffTitle] = useState("");

  // NEW: copy status for diff preview
  const [diffCopyStatus, setDiffCopyStatus] = useState("");
  const [allDiffsLoading, setAllDiffsLoading] = useState(false);
  const [allDiffsStatus, setAllDiffsStatus] = useState("");
  const [manualCopyOpen, setManualCopyOpen] = useState(false);
  const [manualCopyTitle, setManualCopyTitle] = useState("");
  const [manualCopyText, setManualCopyText] = useState("");
  const manualCopyRef = useRef(null);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewErr, setReviewErr] = useState("");
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewTargetPath, setReviewTargetPath] = useState("");

  const importedWithStatus = useMemo(() => {
    return (imported || []).map((f) => ({
      ...f,
      _status: f._status || "ready", // ready | saved | error
      _message: f._message || "",
    }));
  }, [imported]);

  const hasSkippedFiles = (skipped || []).length > 0;

  const reviewableFiles = useMemo(() => {
    const byPath = new Map();

    (importedWithStatus || []).forEach((file) => {
      if (!file || !file.path) return;
      byPath.set(file.path, {
        file_id: file.file_id,
        path: file.path,
        module: file.module,
        original_content: file.original_content || "",
        updated_content: file.updated_content || "",
      });
    });

    (skipped || []).forEach((file) => {
      if (!file || !file.path) return;
      if (!file.can_create) return;
      if (!Object.prototype.hasOwnProperty.call(file, "updated_content")) return;
      if (byPath.has(file.path)) return;
      byPath.set(file.path, {
        file_id: file.file_id || null,
        path: file.path,
        module: file.module || "",
        original_content: file.original_content || "",
        updated_content: file.updated_content || "",
      });
    });

    return Array.from(byPath.values());
  }, [importedWithStatus, skipped]);

  useEffect(() => {
    if (!importedWithStatus.length) {
      setReviewTargetPath("");
      return;
    }

    const stillExists = importedWithStatus.some((f) => f.path === reviewTargetPath);
    if (!reviewTargetPath || !stillExists) {
      setReviewTargetPath(importedWithStatus[0].path || "");
    }
  }, [importedWithStatus, reviewTargetPath]);

  useEffect(() => {
    if (!manualCopyOpen || !manualCopyRef.current) return;

    const timer = window.setTimeout(() => {
      try {
        manualCopyRef.current.focus();
        manualCopyRef.current.select();
        manualCopyRef.current.setSelectionRange(0, manualCopyText.length);
      } catch (e) {
        // no-op
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [manualCopyOpen, manualCopyText]);

  const resetState = () => {
    setZipFile(null);
    setLoading(false);
    setErr("");
    setImported([]);
    setSkipped([]);
    setCreatingMap({});
    setCreateInfo("");
    setDiffOpen(false);
    setDiffLoading(false);
    setDiffErr("");
    setDiffText("");
    setDiffTitle("");
    setDiffCopyStatus(""); // NEW
    setAllDiffsLoading(false);
    setAllDiffsStatus("");
    setManualCopyOpen(false);
    setManualCopyTitle("");
    setManualCopyText("");
    setReviewOpen(false);
    setReviewLoading(false);
    setReviewErr("");
    setReviewResult(null);
    setReviewTargetPath("");
    setChangeSummary("Imported from ZIP");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUpload = async () => {
    if (!zipFile) {
      setErr("Choose a ZIP file first.");
      return;
    }

    setLoading(true);
    setErr("");
    setImported([]);
    setSkipped([]);

    try {
      const form = new FormData();
      form.append("zip_file", zipFile);
      if (selectedProjectId) form.append("project_id", selectedProjectId);

      const res = await fetch("/api/dev-ide/import-zip", {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to import zip.");
      }

      const list = (data.imported || []).map((f) => ({
        ...f,
        _status: "ready",
        _message: "",
      }));
      setImported(list);
      setSkipped(data.skipped || []);
      setCreateInfo("");
      setReviewErr("");
      setReviewResult(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMissingFile = async (skippedItem) => {
    if (!skippedItem || !skippedItem.path) return;
    if (!zipFile) {
      setErr("Choose a ZIP file first.");
      return;
    }

    const p = skippedItem.path;
    setCreatingMap((prev) => ({ ...(prev || {}), [p]: true }));
    setCreateInfo("");

    try {
      const body = {
        project_id: selectedProjectId || null,
        change_id: activeChange && activeChange.id ? activeChange.id : null,
        username: currentUsername || "dev-ide",
        files: [
          {
            path: skippedItem.path,
            updated_content: skippedItem.updated_content || "",
          },
        ],
      };

      const res = await fetch("/api/dev-ide/import-zip-create-missing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to create missing file.");
      }

      const createdCount = (data.created || []).length;
      const scanSummary = [];
      if (data.scans && data.scans.backend) scanSummary.push("backend scan");
      if (data.scans && data.scans.frontend) scanSummary.push("frontend scan");

      setCreateInfo(
        `Created ${createdCount || 1} file(s) and ran ${
          scanSummary.join(" + ") || "scan"
        }. Re-importing ZIP...`
      );

      // Re-run import so the new file should now appear in the imported list.
      await handleUpload();
    } catch (e) {
      setCreateInfo(`Create failed for ${p}: ${e.message}`);
    } finally {
      setCreatingMap((prev) => {
        const next = { ...(prev || {}) };
        delete next[p];
        return next;
      });
    }
  };

  const openManualCopyDialog = (title, text) => {
    setManualCopyTitle(title || "Copy diff");
    setManualCopyText(text || "");
    setManualCopyOpen(true);
  };

  const isLikelyIOSWebKitClipboardEnvironment = () => {
    if (typeof navigator === "undefined") return false;

    const ua = navigator.userAgent || "";
    const vendor = navigator.vendor || "";
    const platform = navigator.platform || "";
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    const isIOSDevice = /iPad|iPhone|iPod/.test(ua)
      || (/Mac/.test(platform) && maxTouchPoints > 1);
    const isAppleWebKit = /Apple/i.test(vendor) || /Safari/i.test(ua);

    return isIOSDevice && isAppleWebKit;
  };

  const tryExecCommandCopy = (text) => {
    if (
      typeof document === "undefined"
      || typeof document.execCommand !== "function"
    ) {
      return false;
    }

    let ta = null;
    try {
      ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "-9999px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      return !!document.execCommand("copy");
    } catch (e) {
      return false;
    } finally {
      if (ta && ta.parentNode) {
        ta.parentNode.removeChild(ta);
      }
    }
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return { ok: false, mode: "empty" };

    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      typeof window !== "undefined" &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
      return { ok: true, mode: "clipboard" };
    }

    if (!isLikelyIOSWebKitClipboardEnvironment() && tryExecCommandCopy(text)) {
      return { ok: true, mode: "execCommand" };
    }

    return { ok: false, mode: "manual" };
  };

  const buildDiffPayload = (file) => ({
    original_content: file.original_content || "",
    updated_content: file.updated_content || "",
    file_id: file.file_id,
    file_path: file.path,
    change_summary: changeSummary || "",
    username: currentUsername || "unknown",
    change_id: activeChange && activeChange.id ? activeChange.id : null,
    project_id: selectedProjectId || null,
  });

  const fetchDiffTextForFile = async (file) => {
    const res = await fetch("/api/dev-ide/preview-diff", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildDiffPayload(file)),
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || `Failed to generate diff for ${file.path || "file"}.`);
    }
    return data.diff || "";
  };

  const formatDiffBlock = (file, diff) => {
    const lines = [`# PATH: ${file.path || "unknown"}`];
    if (file.module) {
      lines.push(`# MODULE: ${file.module}`);
    }
    lines.push("");
    lines.push(diff || "(no diff)");
    return lines.join("\n");
  };

  const buildAllDiffsText = async () => {
    const files = importedWithStatus || [];
    const chunks = [];

    for (const file of files) {
      const diff = await fetchDiffTextForFile(file);
      chunks.push(formatDiffBlock(file, diff));
    }

    return chunks.join("\n\n" + "=".repeat(80) + "\n\n");
  };

  const handlePreviewDiff = async (file) => {
    if (!file) return;
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(file.path || "Diff");
    setDiffCopyStatus(""); // NEW
    setAllDiffsLoading(false);
    setAllDiffsStatus("");

    try {
      const diff = await fetchDiffTextForFile(file);
      setDiffText(diff);
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  // NEW: copy current preview diff to clipboard
  const handleCopyDiff = async () => {
    const path = diffTitle || "";
    const diff = diffText || "";
    if (!path && !diff) return;

    const textToCopy = `# PATH: ${path}\n\n${diff}`;

    try {
      const result = await copyTextToClipboard(textToCopy);
      if (result.ok) {
        setDiffCopyStatus("Copied to clipboard");
        setTimeout(() => setDiffCopyStatus(""), 2000);
        return;
      }

      openManualCopyDialog(path || "Diff", textToCopy);
      setDiffCopyStatus("Manual copy mode opened");
      setTimeout(() => setDiffCopyStatus(""), 2500);
    } catch (e) {
      openManualCopyDialog(path || "Diff", textToCopy);
      setDiffCopyStatus("Manual copy mode opened");
      setTimeout(() => setDiffCopyStatus(""), 2500);
    }
  };

  const handlePreviewAllDiffs = async () => {
    if (!importedWithStatus.length) return;

    setDiffOpen(true);
    setDiffLoading(true);
    setDiffErr("");
    setDiffText("");
    setDiffTitle(`All imported diffs (${importedWithStatus.length} file${importedWithStatus.length === 1 ? "" : "s"})`);
    setDiffCopyStatus("");
    setAllDiffsStatus("");

    try {
      const allDiffsText = await buildAllDiffsText();
      setDiffText(allDiffsText || "(no diffs)");
    } catch (e) {
      setDiffErr(e.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleCopyAllDiffs = async () => {
    if (!importedWithStatus.length) return;

    setAllDiffsLoading(true);
    setAllDiffsStatus("");

    let allDiffsText = "";

    try {
      allDiffsText = await buildAllDiffsText();
      const result = await copyTextToClipboard(allDiffsText);
      if (result.ok) {
        setAllDiffsStatus("All diffs copied to clipboard");
        setTimeout(() => setAllDiffsStatus(""), 2500);
        return;
      }

      openManualCopyDialog(`All imported diffs (${importedWithStatus.length})`, allDiffsText);
      setAllDiffsStatus("Manual copy mode opened");
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } catch (e) {
      if (allDiffsText) {
        openManualCopyDialog(`All imported diffs (${importedWithStatus.length})`, allDiffsText);
        setAllDiffsStatus("Manual copy mode opened");
      } else {
        setAllDiffsStatus(e.message || "Copy all diffs failed");
      }
      setTimeout(() => setAllDiffsStatus(""), 3000);
    } finally {
      setAllDiffsLoading(false);
    }
  };

  const handleRunReview = async () => {
    if (!importedWithStatus.length) {
      setReviewErr("Import modularised files first.");
      setReviewOpen(true);
      return;
    }

    if (!reviewTargetPath) {
      setReviewErr("Choose the target file being modularised.");
      setReviewOpen(true);
      return;
    }

    setReviewOpen(true);
    setReviewLoading(true);
    setReviewErr("");

    try {
      const review = await runImportZipModulariseReview({
        files: reviewableFiles,
        targetFilePath: reviewTargetPath,
        changeSummary,
        projectId: selectedProjectId || null,
      });

      setReviewResult(review || null);
    } catch (e) {
      setReviewErr(e.message);
      setReviewResult(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleSaveFile = async (file) => {
    if (!file) return;
    if (!changeSummary || !changeSummary.trim()) {
      setErr("Enter a change summary before saving.");
      return;
    }

    // optimistic mark row as saving
    setImported((prev) =>
      (prev || []).map((f) =>
        f.path === file.path
          ? { ...f, _status: "saving", _message: "Saving..." }
          : f
      )
    );

    try {
      const body = {
        file_id: file.file_id,
        original_content: file.original_content || "",
        updated_content: file.updated_content || "",
        change_summary: changeSummary.trim(),
        username: currentUsername || "unknown",
        change_id: activeChange && activeChange.id ? activeChange.id : null,
        project_id: selectedProjectId || null,
      };

      const res = await fetch("/api/dev-ide/save-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save file.");
      }

      const result = data.result || {};
      const msg = `Saved. Backup: ${result.backup_file || "n/a"}`;
      setImported((prev) =>
        (prev || []).map((f) =>
          f.path === file.path
            ? { ...f, _status: "saved", _message: msg }
            : f
        )
      );

      if (typeof onImportedSaved === "function") {
        onImportedSaved();
      }
    } catch (e) {
      setImported((prev) =>
        (prev || []).map((f) =>
          f.path === file.path
            ? { ...f, _status: "error", _message: e.message }
            : f
        )
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import files from ZIP</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload a ZIP that contains repo-relative paths (e.g.{" "}
            <code>backend/...</code> or <code>src/...</code>). Files must exist
            in the Dev IDE file map to be importable.
          </Typography>

          {err ? <Alert severity="error">{err}</Alert> : null}
          {createInfo ? (
            <Alert
              severity={
                createInfo.toLowerCase().includes("create failed")
                  ? "error"
                  : "info"
              }
            >
              {createInfo}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" component="label">
              Choose ZIP
              <input
                hidden
                type="file"
                accept=".zip"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  setZipFile(f || null);
                }}
              />
            </Button>
            <Typography variant="body2" sx={{ alignSelf: "center" }}>
              {zipFile ? zipFile.name : "No file selected"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!zipFile || loading}
            >
              Import
            </Button>
          </Stack>

          <TextField
            label="Change summary (used for Save)"
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            size="small"
            fullWidth
          />

          {importedWithStatus.length > 0 ? (
            <>
              {hasSkippedFiles ? (
                <Alert severity="info">
                  Verify modularisation will include skipped new files using their full ZIP content, even before they are created. Create them before save if the review passes.
                </Alert>
              ) : null}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                select
                label="Target file being modularised"
                value={reviewTargetPath}
                onChange={(e) => setReviewTargetPath(e.target.value)}
                size="small"
                sx={{ minWidth: { md: 420 } }}
              >
                {importedWithStatus.map((f) => (
                  <MenuItem key={f.path} value={f.path}>
                    {f.path}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={handleRunReview}
                disabled={loading || reviewLoading || !reviewTargetPath}
              >
                Verify modularisation
              </Button>
              </Stack>
            </>
          ) : null}

          {loading ? <LinearProgress /> : null}

          <Divider />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Typography variant="subtitle2">
              Imported files ({importedWithStatus.length})
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              onClick={handlePreviewAllDiffs}
              disabled={!importedWithStatus.length || loading || allDiffsLoading}
            >
              Review all diffs
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCopyAllDiffs}
              disabled={!importedWithStatus.length || loading || allDiffsLoading}
            >
              {allDiffsLoading ? "Copying..." : "Copy all diffs"}
            </Button>
          </Stack>

          {allDiffsStatus ? (
            <Alert
              severity={allDiffsStatus.toLowerCase().includes("failed") ? "error" : "success"}
            >
              {allDiffsStatus}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Path</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importedWithStatus.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No imported files yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  importedWithStatus.map((f) => (
                    <TableRow key={f.path}>
                      <TableCell
                        sx={{
                          maxWidth: 520,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {f.path}
                      </TableCell>
                      <TableCell>{f.module || ""}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="caption">
                            {f._status === "saving" ? "saving" : f._status}
                          </Typography>
                          {f._message ? (
                            <Typography
                              variant="caption"
                              color={
                                f._status === "error" ? "error" : "text.secondary"
                              }
                            >
                              {f._message}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handlePreviewDiff(f)}
                            disabled={loading}
                          >
                            Preview diff
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSaveFile(f)}
                            disabled={
                              f._status === "saved" ||
                              f._status === "saving" ||
                              loading
                            }
                          >
                            Save
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          {skipped && skipped.length > 0 ? (
            <>
              <Typography variant="subtitle2">Skipped ({skipped.length})</Typography>
              <Paper variant="outlined" sx={{ p: 1 }}>
                {(skipped || []).slice(0, 20).map((s, idx) => {
                  const canCreate = !!s.can_create;
                  const isCreating = !!creatingMap[(s.path || "").toString()];

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={`${s.path}: ${s.reason}`}
                      >
                        {s.path}: {s.reason}
                      </Typography>
                      {canCreate ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleCreateMissingFile(s)}
                          disabled={loading || isCreating}
                        >
                          {isCreating ? "Creating..." : "Create file"}
                        </Button>
                      ) : null}
                    </Box>
                  );
                })}
                {skipped.length > 20 ? (
                  <Typography variant="caption" color="text.secondary">
                    ...and {skipped.length - 20} more
                  </Typography>
                ) : null}
              </Paper>
            </>
          ) : null}
        </Stack>

        {/* Diff Preview */}
        <Dialog
          open={diffOpen}
          onClose={() => {
            setDiffOpen(false);
            setDiffCopyStatus(""); // NEW
    setAllDiffsLoading(false);
    setAllDiffsStatus("");
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Diff: {diffTitle}</DialogTitle>
          <DialogContent>
            {diffLoading ? <LinearProgress /> : null}
            {diffErr ? <Alert severity="error">{diffErr}</Alert> : null}
            {diffCopyStatus ? (
              <Alert
                severity={diffCopyStatus === "Copy failed" ? "error" : diffCopyStatus === "Manual copy mode opened" ? "info" : "success"}
                sx={{ mt: 1 }}
              >
                {diffCopyStatus}
              </Alert>
            ) : null}
            <Paper
              variant="outlined"
              sx={{ p: 1, mt: 1, maxHeight: "60vh", overflow: "auto" }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {diffText || "(no diff)"}
              </pre>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCopyDiff}
              variant="outlined"
              disabled={diffLoading || (!diffTitle && !diffText)}
            >
              Copy
            </Button>
            <Button onClick={() => setDiffOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>



        <Dialog
          open={manualCopyOpen}
          onClose={() => setManualCopyOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Manual copy: {manualCopyTitle}</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              Clipboard write is blocked or unreliable in this device/browser context. We attempt to select the diff text automatically. On iPhone, tap and hold in the text box, then tap Copy.
            </Alert>
            <TextField
              inputRef={manualCopyRef}
              value={manualCopyText}
              onChange={() => {}}
              fullWidth
              multiline
              minRows={16}
              maxRows={24}
              margin="normal"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", alignItems: "flex-start" },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (!manualCopyRef.current) return;
                manualCopyRef.current.focus();
                manualCopyRef.current.select();
                manualCopyRef.current.setSelectionRange(0, manualCopyText.length);
              }}
              variant="outlined"
            >
              Select all
            </Button>
            <Button onClick={() => setManualCopyOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <ImportZipModulariseReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onRunReview={handleRunReview}
          loading={reviewLoading}
          error={reviewErr}
          review={reviewResult}
          targetFilePath={reviewTargetPath}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportZipDialog;